import React, { useState, useRef, useEffect } from 'react';
import performanceMonitor from '../../utils/performanceMonitor';
import smartImagePreloader from '../../utils/smartImagePreloader';
import './ProgressiveImage.css';

const ProgressiveImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = null,
  onLoad = null,
  onError = null,
  context = 'unknown',
  ...props 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loadStartTime, setLoadStartTime] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (src) {
      setImageSrc(src);
      setLoadStartTime(Date.now());
      
      // Check if image is already preloaded
      if (smartImagePreloader.isPreloaded(src)) {
        console.log(`⚡ Instant display: ${src} (preloaded)`);
        setImageLoaded(true);
        if (onLoad) onLoad();
        return;
      }
      
      // If not preloaded, load it normally
      setImageLoaded(false);
      setImageError(false);
      
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
        if (loadStartTime) {
          const loadTime = Date.now() - loadStartTime;
          performanceMonitor.trackImageLoad(src, loadTime, context);
        }
        if (onLoad) onLoad();
      };
      img.onerror = () => {
        setImageError(true);
        if (onError) onError();
      };
      img.src = src;
    }
  }, [src, context, onLoad, onError]);



  return (
    <div className={`progressive-image-container ${className}`}>
      {/* Placeholder/loading state */}
      {!imageLoaded && !imageError && (
        <div className="image-placeholder">
          {placeholder ? (
            <img 
              src={placeholder} 
              alt="Loading..." 
              className="placeholder-image"
            />
          ) : (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {imageError && (
        <div className="image-error">
          <div className="error-icon">⚠️</div>
          <span>Failed to load image</span>
        </div>
      )}

      {/* Actual image */}
      {imageSrc && !imageError && imageLoaded && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          className="progressive-image loaded"
          {...props}
        />
      )}
    </div>
  );
};

export default ProgressiveImage;
